import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Search, 
  Calendar, 
  MessageCircle, 
  Car, 
  CheckCircle,
  ArrowRight,
  Users
} from "lucide-react";

export const TimelyHowItWorks = () => {
  const steps = [
    {
      step: "01",
      icon: <Search className="w-8 h-8" />,
      title: "Find Wash Centers",
      description: "Search for nearby wash centers or browse through our curated list of premium service providers",
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "bg-blue-500",
      textColor: "text-blue-700"
    },
    {
      step: "02", 
      icon: <Calendar className="w-8 h-8" />,
      title: "Book Your Slot",
      description: "Choose your preferred time slot and service type. Book instantly with just a few taps",
      color: "bg-gradient-to-br from-green-50 to-green-100",
      iconColor: "bg-green-500",
      textColor: "text-green-700"
    },
    {
      step: "03",
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Get WhatsApp Updates",
      description: "Receive booking confirmation, center details, and real-time updates on WhatsApp",
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconColor: "bg-purple-500",
      textColor: "text-purple-700"
    },
    {
      step: "04",
      icon: <Car className="w-8 h-8" />,
      title: "Drop Your Vehicle",
      description: "Visit the wash center at your scheduled time and drop off your vehicle safely",
      color: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "bg-orange-500",
      textColor: "text-orange-700"
    },
    {
      step: "05",
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Service Complete",
      description: "Get notified when your vehicle is ready. Pay securely and collect your sparkling clean vehicle",
      color: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      iconColor: "bg-emerald-500",
      textColor: "text-emerald-700"
    }
  ];

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <Badge variant="secondary" className="bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200 mb-4 sm:mb-6 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            Simple Process
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8 leading-tight px-2">
            How It Works
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-2">
            Getting your vehicle washed has never been easier. Follow these simple steps to book your wash and enjoy a seamless experience.
          </p>
        </div>

        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-green-200 to-orange-200 z-0 rounded-full"></div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center group">
                <Card className="w-full max-w-sm group-hover:shadow-2xl transition-all duration-500 border-0 bg-white/80 backdrop-blur-sm hover:bg-white/90 transform hover:-translate-y-2">
                  <CardContent className="p-5 sm:p-6 lg:p-8 text-center">
                    {/* Step Number */}
                    <div className="relative mb-6 sm:mb-8">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl mb-4 sm:mb-6 mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                        {step.step}
                      </div>
                      <div className={`w-14 h-14 sm:w-16 sm:h-16 ${step.color} rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 ${step.iconColor} rounded-lg sm:rounded-xl flex items-center justify-center`}>
                          <div className="text-white">
                            {step.icon}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 group-hover:text-gray-800 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Arrow for mobile */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center my-4">
                    <ArrowRight className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12 lg:mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-3xl p-6 sm:p-8 lg:p-10 max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">
              Ready to Get Started?
            </h3>
            <p className="text-base sm:text-lg text-gray-600 mb-6 break-words">
              Join thousands of satisfied customers who trust Timelyyy for their vehicle wash needs.
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center max-w-2xl mx-auto">
              <Link to="/booking" className="w-full md:flex-1">
                <Button size="lg" className="w-full h-auto min-h-[52px] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Car className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Book Your Wash Now</span>
                </Button>
              </Link>
              <Link to="/onboard-vendor" className="w-full md:flex-1">
                <Button size="lg" variant="outline" className="w-full h-auto min-h-[52px] border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-6 sm:px-8 py-3.5 text-base sm:text-lg font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Join as Vendor</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
